import React, { useEffect, useRef } from 'react'
import { Badge } from '../ui/badge'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Button } from '../ui/button'
import useCart from '@/contexts/useCart'
import { ShoppingCart } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import CartContent from './cart-content'
import { useAnalytics } from '@/hooks/use-analytics'
import { EventName } from '@/types/analytics'

const CartDrawer = ({
  checkoutUrl,
  goToCart,
  size = 'icon',
}: {
  checkoutUrl: string
  goToCart?: () => void
  size?: 'default' | 'sm' | 'lg' | 'icon' | null | undefined
}) => {
  const { t } = useTranslation('experience')
  const { masterOrder, isDrawerOpen, setDrawerOpen } = useCart()
  const { trackOrder } = useAnalytics()
  const hasTrackedView = useRef(false)
  const cartOpenTime = useRef<number | null>(null)
  const MIN_VIEW_TIME = 3000 // 3 seconds minimum to consider it a real abandonment

  // Track cart view when drawer opens
  useEffect(() => {
    if (isDrawerOpen && masterOrder && !hasTrackedView.current) {
      trackOrder(EventName.CartView, {
        orderId: masterOrder.id.toString(),
        amount: masterOrder.amount,
        currency: 'EUR',
        itemCount: masterOrder.orders?.length || 0,
        isFree: masterOrder.amount === 0,
      })
      hasTrackedView.current = true
      cartOpenTime.current = Date.now()
    }

    // Track abandonment when drawer closes with items still in cart
    if (!isDrawerOpen && hasTrackedView.current && masterOrder && masterOrder.orders?.length > 0) {
      const viewDuration = cartOpenTime.current ? Date.now() - cartOpenTime.current : 0

      // Only track if they viewed for more than minimum time
      if (viewDuration >= MIN_VIEW_TIME) {
        trackOrder(EventName.CartAbandonment, {
          orderId: masterOrder.id.toString(),
          amount: masterOrder.amount,
          currency: 'EUR',
          itemCount: masterOrder.orders?.length || 0,
          isFree: masterOrder.amount === 0,
          viewDuration: Math.floor(viewDuration / 1000), // in seconds
        })
      }

      hasTrackedView.current = false
      cartOpenTime.current = null
    }
  }, [isDrawerOpen, masterOrder, trackOrder])

  if (goToCart) {
    return (
      <Button
        size={size}
        variant={size === 'icon' ? 'ghost' : 'default'}
        className="relative"
        id="cart-drawer-button"
        onClick={goToCart}
      >
        {size === 'icon' ? (
          <ShoppingCart size={16} />
        ) : (
          <span>{t('view_cart')}</span>
        )}

        {masterOrder?.orders?.length ? (
          <Badge
            className="rounded-full absolute w-4 h-4 justify-center px-1 top-1 -right-1"
            variant={size === 'icon' ? 'default' : 'secondary'}
          >
            {masterOrder.orders.length}
          </Badge>
        ) : null}
      </Button>
    )
  }

  return (
    <Sheet open={isDrawerOpen} onOpenChange={setDrawerOpen}>
      <SheetTrigger asChild>
        <Button
          size={size}
          variant={size === 'icon' ? 'ghost' : 'default'}
          className="relative"
        >
          {size === 'icon' ? (
            <ShoppingCart size={16} />
          ) : (
            <span>{t('view_cart')}</span>
          )}

          {masterOrder?.orders?.length ? (
            <Badge
              className="rounded-full absolute w-4 h-4 justify-center px-1 top-1 -right-1"
              variant={size === 'icon' ? 'default' : 'secondary'}
            >
              {masterOrder.orders.length}
            </Badge>
          ) : null}
        </Button>
      </SheetTrigger>

      <SheetContent className="max-w-full w-full md:max-w-[480px] flex flex-col">
        {/* 顶部标题 */}
        <SheetHeader>
          <SheetTitle>{t('cart', { ns: 'common' })}</SheetTitle>
        </SheetHeader>

        <CartContent checkoutUrl={checkoutUrl} />
      </SheetContent>
    </Sheet>
  )
}

export default CartDrawer
